import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import PageHero from 'tio-ui/components/page-hero';
import AlertBanner from 'tio-ui/components/alert-banner';
import EmptyState from 'tio-ui/components/empty-state';
import IconBadge from 'tio-ui/components/icon-badge';
import FaqAccordion from 'tio-ui/components/faq-accordion';
import {
  PhArrowLeft,
  PhCheckCircle,
  PhClock,
  PhFileSearch,
  PhHeart,
  PhInfo,
  PhSparkle,
  PhStar,
  PhTrendUp,
  PhWarning,
} from 'ember-phosphor-icons';

const SAMPLE_FAQ = [
  {
    question: 'What is tuition assistance?',
    answer:
      'Tuition assistance is a benefit that helps cover the cost of education and training programs.',
  },
  {
    question: 'How do I apply?',
    answer: 'Navigate to the TAS section and submit an application with your program details.',
  },
  {
    question: 'What programs are eligible?',
    answer:
      'Eligible programs include degree programs, certifications, and approved professional development courses.',
  },
];

<template>
  {{! template-lint-disable no-bare-strings }}
  <VStack class="w-full min-w-0">
    <div class="mb-4">
      <LinkTo @route="authenticated.reimagined.components" class="tio-demo-link">
        <PhArrowLeft @size={{18}} />
        Back to Components
      </LinkTo>
    </div>

    <PageHero
      @title="Feedback & Status"
      @subtitle="Contextual alerts, empty states, icon badges, and FAQ accordions."
      @icon={{PhWarning}}
      @gradient="warm"
      @decoration="circles"
    >
      <:actions>
        <Chip @intent="primary">4 Components</Chip>
      </:actions>
    </PageHero>

    <div class="space-y-8">

      {{! ========================================
            AlertBanner
            ======================================== }}
      <Section>
        <:header>
          <div class="tio-row-tight">
            <PhWarning @size={{20}} class="tio-fg-brand" />
            <span>AlertBanner</span>
            <Chip @size="sm" @intent="primary">4 Intents</Chip>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            Contextual alert/notice banner with automatic icon mapping. Supports warning, info,
            success, and tip intents with optional title and action block.
          </Text>

          <div class="tio-bg-sunken rounded-lg p-6 mb-4 space-y-3">
            <AlertBanner @intent="warning" @title="Action Required">
              Your application deadline is approaching. Please submit by Feb 15.
            </AlertBanner>
            <AlertBanner @intent="info">
              Your TA benefit renews on January 1st each year.
            </AlertBanner>
            <AlertBanner @intent="success" @title="Application Approved">
              Your tuition assistance has been approved for $2,400.
            </AlertBanner>
            <AlertBanner @intent="tip">
              <:default>
                Pro tip: You can apply for multiple programs in the same benefit year.
              </:default>
              <:action>
                <Button @intent="primary" @size="sm" @appearance="outlined">Learn More</Button>
              </:action>
            </AlertBanner>
          </div>

          <details class="border rounded-lg">
            <summary class="tio-demo-disclosure-summary">
              Code Examples
            </summary>
            <pre class="tio-demo-code-block"><code>&lt;AlertBanner @intent="warning" @title="Action
                Required"&gt; Deadline is approaching. &lt;/AlertBanner&gt; &lt;AlertBanner
                @intent="tip"&gt; &lt;:default&gt;Pro tip: Apply for multiple
                programs.&lt;/:default&gt; &lt;:action&gt;&lt;Button @size="sm"&gt;Learn
                More&lt;/Button&gt;&lt;/:action&gt; &lt;/AlertBanner&gt;</code></pre>
          </details>
        </:body>
      </Section>

      {{! ========================================
            EmptyState
            ======================================== }}
      <Section>
        <:header>
          <div class="tio-row-tight">
            <PhFileSearch @size={{20}} class="tio-fg-brand" />
            <span>EmptyState</span>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            Placeholder for empty lists or data sections. Icon, title, description, and optional
            action. Supports compact mode for inline use.
          </Text>

          <div class="tio-bg-sunken rounded-lg p-6 mb-4 space-y-4">
            <EmptyState
              @icon={{PhFileSearch}}
              @title="No applications yet"
              @description="Start by exploring eligible programs and submitting your first application."
              @color="violet"
            >
              <:action>
                <Button @intent="primary" @size="sm">Browse Programs</Button>
              </:action>
            </EmptyState>

            <Text @size="sm" @weight="semibold" class="tio-fg-secondary">Compact:</Text>
            <EmptyState
              @icon={{PhStar}}
              @title="No favorites saved"
              @compact={{true}}
              @color="gray"
            />
          </div>

          <details class="border rounded-lg">
            <summary class="tio-demo-disclosure-summary">
              Code Examples
            </summary>
            <pre class="tio-demo-code-block"><code>&lt;EmptyState
                @icon=&#123;&#123;PhFileSearch&#125;&#125; @title="No applications yet"
                @description="Start exploring programs." @color="violet"&gt;
                &lt;:action&gt;&lt;Button&gt;Browse Programs&lt;/Button&gt;&lt;/:action&gt;
                &lt;/EmptyState&gt;</code></pre>
          </details>
        </:body>
      </Section>

      {{! ========================================
            IconBadge
            ======================================== }}
      <Section>
        <:header>
          <div class="tio-row-tight">
            <PhSparkle @size={{20}} class="tio-fg-brand" />
            <span>IconBadge</span>
            <Chip @size="sm" @intent="success">11 Colors</Chip>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            Colored circular icon badge. Building block for lists, cards, and section headers.
          </Text>

          <div class="tio-bg-sunken rounded-lg p-6 mb-4">
            <div class="tio-flex-loose items-end">
              <div class="text-center">
                <IconBadge @icon={{PhStar}} @color="violet" @size="lg" />
                <Text @size="xs" @variant="muted" class="mt-1">lg</Text>
              </div>
              <div class="text-center">
                <IconBadge @icon={{PhCheckCircle}} @color="emerald" @size="md" />
                <Text @size="xs" @variant="muted" class="mt-1">md</Text>
              </div>
              <div class="text-center">
                <IconBadge @icon={{PhClock}} @color="amber" @size="sm" />
                <Text @size="xs" @variant="muted" class="mt-1">sm</Text>
              </div>
              <div class="text-center">
                <IconBadge @icon={{PhTrendUp}} @color="blue" @size="md" />
                <Text @size="xs" @variant="muted" class="mt-1">blue</Text>
              </div>
              <div class="text-center">
                <IconBadge @icon={{PhHeart}} @color="pink" @size="md" />
                <Text @size="xs" @variant="muted" class="mt-1">pink</Text>
              </div>
              <div class="text-center">
                <IconBadge @icon={{PhWarning}} @color="red" @size="md" />
                <Text @size="xs" @variant="muted" class="mt-1">red</Text>
              </div>
            </div>
          </div>

          <details class="border rounded-lg">
            <summary class="tio-demo-disclosure-summary">
              Code Examples
            </summary>
            <pre class="tio-demo-code-block"><code>&lt;IconBadge
                @icon=&#123;&#123;PhStar&#125;&#125; @color="violet" @size="lg" /&gt; &lt;IconBadge
                @icon=&#123;&#123;PhCheckCircle&#125;&#125; @color="emerald" @size="md" /&gt;</code></pre>
          </details>
        </:body>
      </Section>

      {{! ========================================
            FaqAccordion
            ======================================== }}
      <Section>
        <:header>
          <div class="tio-row-tight">
            <PhInfo @size={{20}} class="tio-fg-brand" />
            <span>FaqAccordion</span>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            Expandable FAQ list. One item open at a time. Accepts an array of question/answer pairs.
          </Text>

          <div class="tio-bg-sunken rounded-lg p-6 mb-4">
            <FaqAccordion @items={{SAMPLE_FAQ}} />
          </div>

          <details class="border rounded-lg">
            <summary class="tio-demo-disclosure-summary">
              Code Examples
            </summary>
            <pre class="tio-demo-code-block"><code>const items = [ { question: 'What is TA?',
                answer: 'Tuition assistance...' }, { question: 'How do I apply?', answer: 'Navigate
                to...' }, ]; &lt;FaqAccordion @items=&#123;&#123;items&#125;&#125; /&gt;</code></pre>
          </details>
        </:body>
      </Section>
    </div>
  </VStack>
</template>
